import {
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react';
import type {OffsetOptions, Placement, Side} from '@floating-ui/react';
import cx from 'classnames';
import type {KeyboardEvent, ReactNode, Ref} from 'react';
import React, {useRef, useState} from 'react';

import Item from './MenuItem.react';
import {MenuProvider, useMenuSafe} from './MenuContext.react';
import {focusMenuItem} from './menuKeyboard';

export interface SubmenuTriggerProps extends Record<string, unknown> {
  ref: Ref<HTMLElement>;
  role: 'menuitem';
  tabIndex: number;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
}

export interface SubmenuTriggerRenderProps {
  triggerProps: SubmenuTriggerProps;
  isOpen: boolean;
}

interface MenuSubmenuProps extends PropsWithChildrenRequired, PropsWithClassNameOptional {
  /** Content of the default `Menu.Item` trigger. Ignored when `renderTrigger` is given. */
  label?: ReactNode;
  /**
   * Renders a custom trigger, such as a `MenuListItem`. Spread `triggerProps` onto the element;
   * it carries the ref, ARIA state, and the hover, click, and key handlers.
   */
  renderTrigger?: (props: SubmenuTriggerRenderProps) => ReactNode;
  /** Side the submenu opens on. It flips when that side lacks room. */
  placement?: Placement;
  disabled?: boolean;
}

const PANEL_OVERLAP = 4;

const OPEN_KEYS = new Set(['Enter', ' ', 'ArrowRight']);
const CLOSE_KEYS = new Set(['Escape', 'ArrowLeft']);

/**
 * How far past the trigger's `side` edge the submenu starts: just over the edge of the menu panel
 * that contains the trigger, or flush with the trigger when it has no parent panel.
 */
const distanceFromTrigger = (trigger: Element, side: Side) => {
  const panel = trigger.parentElement?.closest('[role="menu"]');
  if (!panel) return 0;
  const item = trigger.getBoundingClientRect();
  const box = panel.getBoundingClientRect();
  const insets = {
    left: item.left - box.left,
    right: box.right - item.right,
    top: item.top - box.top,
    bottom: box.bottom - item.bottom
  };
  return insets[side] - PANEL_OVERLAP;
};

/**
 * Overlaps the parent panel's edge on whichever side the submenu lands. With the default
 * `-start` alignment, the submenu's top edge lines up with the trigger's.
 */
const panelOffset: OffsetOptions = ({placement: finalPlacement, elements}) => {
  const side = finalPlacement.split('-')[0] as Side;
  const trigger = elements.reference;
  return trigger instanceof Element ? distanceFromTrigger(trigger, side) : 0;
};

/**
 * A menu item that opens a nested menu of its own, positioned with Floating UI.
 *
 * Works inside `Menu.Content`, `Menu.Static`, or any list of `MenuListItem`s. Opens on hover
 * (with a safe triangle toward the submenu), click, or Enter/Space/ArrowRight; ArrowLeft and
 * Escape close it and return focus to the trigger. The submenu renders in place rather than in a
 * portal, so outside-click handling on the parent treats it as part of the parent menu. Submenus
 * nest: put a `Menu.Submenu` among another's children for a further level.
 */
const MenuSubmenu = ({
  children,
  className,
  label,
  renderTrigger,
  placement = 'right-start',
  disabled = false
}: MenuSubmenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const focusFirstOnOpen = useRef(false);
  const menu = useMenuSafe();

  const {refs, floatingStyles, context, elements} = useFloating({
    open: isOpen,
    onOpenChange: (nextOpen) => {
      if (!nextOpen && elements.floating?.contains(document.activeElement)) {
        (elements.reference as HTMLElement | null)?.focus();
      }
      setIsOpen(nextOpen);
    },
    placement,
    strategy: 'fixed',
    middleware: [offset(panelOffset), flip(), shift({padding: 8})],
    whileElementsMounted: autoUpdate
  });

  const hover = useHover(context, {enabled: !disabled, handleClose: safePolygon()});
  const click = useClick(context, {enabled: !disabled});
  const dismiss = useDismiss(context, {escapeKey: false});
  const role = useRole(context, {role: 'menu'});
  const {getReferenceProps, getFloatingProps} = useInteractions([hover, click, dismiss, role]);

  const setPanel = (panel: HTMLDivElement | null) => {
    refs.setFloating(panel);
    if (panel && focusFirstOnOpen.current) {
      focusFirstOnOpen.current = false;
      focusMenuItem(panel, 'first');
    }
  };

  const closeAndFocusTrigger = () => {
    setIsOpen(false);
    (elements.reference as HTMLElement | null)?.focus();
  };

  const handleTriggerKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (disabled || !OPEN_KEYS.has(e.key)) return;
    e.preventDefault();
    e.stopPropagation();
    if (isOpen && elements.floating) {
      focusMenuItem(elements.floating, 'first');
      return;
    }
    focusFirstOnOpen.current = true;
    setIsOpen(true);
  };

  const handlePanelKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!elements.floating) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      focusMenuItem(elements.floating, e.key === 'ArrowDown' ? 'next' : 'previous');
    } else if (CLOSE_KEYS.has(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      closeAndFocusTrigger();
    }
  };

  // oxlint-disable-next-line react/refs -- floating-ui's refs.setReference is a stable callback ref, not a render-time ref read
  const triggerProps = getReferenceProps({
    ref: refs.setReference,
    role: 'menuitem',
    tabIndex: disabled ? -1 : 0,
    'aria-haspopup': 'menu',
    'aria-expanded': isOpen,
    'aria-disabled': disabled || undefined,
    onKeyDown: handleTriggerKeyDown
  }) as SubmenuTriggerProps;

  const trigger = renderTrigger ? (
    renderTrigger({triggerProps, isOpen})
  ) : (
    <Item
      {...triggerProps}
      className={cx({'o-menu__item--open': isOpen})}
      disabled={disabled}
      rightIconProps={{icon: placement.startsWith('left') ? 'chevron-left' : 'chevron-right'}}
    >
      {label}
    </Item>
  );

  return (
    <>
      {/* The trigger must not close the parent menu when clicked, so hide closeOnItemClick from it. */}
      {menu ? (
        <MenuProvider value={{...menu, closeOnItemClick: false}}>{trigger}</MenuProvider>
      ) : (
        trigger
      )}
      {isOpen && (
        <div
          ref={setPanel}
          style={floatingStyles}
          {...getFloatingProps({onKeyDown: handlePanelKeyDown})}
          className={cx(
            'o-menu-content o-menu-submenu z-1 rounded-x1 border-border-primary background-primary p-x1 shadow-l2 border border-solid',
            className
          )}
        >
          {children}
        </div>
      )}
    </>
  );
};

MenuSubmenu.displayName = 'MenuSubmenu';

export default MenuSubmenu;
